// src/Dashboard/pages/user/Absences/utils/absenceUtils.js

import {
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaUserCheck,
  FaUserInjured,
} from "react-icons/fa";

export const todayStart = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

export const parseDate = (value) => {
  if (!value) return null;

  const date =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  date.setHours(0, 0, 0, 0);
  return date;
};

export const formatDate = (value) => {
  const date = parseDate(value);

  if (!date) return "Non précisée";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const toInputDate = (value) => {
  if (!value) return "";

  if (typeof value === "string" && value.includes("T")) {
    return value.split("T")[0];
  }

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().split("T")[0];
};

export const normalizeText = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

export const getApiBaseUrl = () => {
  try {
    return (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  } catch {
    return "";
  }
};

export const toAbsolutePhotoUrl = (url) => {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) return value;

  return `${getApiBaseUrl()}${value}`;
};

export const getAdherentName = (adherent) =>
  `${adherent?.prenom || ""} ${adherent?.nom || ""}`.trim();

export const isAdherentAbsent = (adherent) =>
  adherent?.is_blessed === true ||
  adherent?.is_blessed === 1 ||
  adherent?.is_blessed === "1" ||
  adherent?.is_blessed === "true";

export const appendNote = (previousNotes, nextNote) =>
  previousNotes ? `${previousNotes} | ${nextNote}` : nextNote;

export const buildAbsenceNote = ({ absenceType, dateReprise, motifAbsence }) => {
  const today = new Date().toLocaleDateString("fr-FR");
  const formattedDate = dateReprise ? formatDate(dateReprise) : "non précisée";

  return `${absenceType} signalé(e) le ${today} jusqu'au ${formattedDate} : ${
    motifAbsence.trim() || "—"
  }`;
};

export const buildReturnNote = () => {
  const today = new Date().toLocaleDateString("fr-FR");
  return `Reprise signalée le ${today}`;
};

export const getAbsenceStatus = (adherent) => {
  const isAbsent = isAdherentAbsent(adherent);
  const returnDate = parseDate(adherent?.date_reprise);
  const today = todayStart();

  if (!isAbsent) {
    return {
      key: "active",
      label: "Actif(ve)",
      description: "Présence normale",
      icon: FaUserCheck,
      badge:
        "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
      card: "border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/20 dark:bg-emerald-500/10",
    };
  }

  if (returnDate && returnDate < today) {
    return {
      key: "returnLate",
      label: "Retour à confirmer",
      description: `Reprise prévue le ${formatDate(returnDate)}`,
      icon: FaExclamationTriangle,
      badge:
        "bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20",
      card: "border-red-200 bg-red-50/70 dark:border-red-500/20 dark:bg-red-500/10",
    };
  }

  if (returnDate && returnDate.getTime() === today.getTime()) {
    return {
      key: "returnToday",
      label: "Reprise aujourd’hui",
      description: `Reprise prévue le ${formatDate(returnDate)}`,
      icon: FaClock,
      badge:
        "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
      card: "border-amber-200 bg-amber-50/70 dark:border-amber-500/20 dark:bg-amber-500/10",
    };
  }

  return {
    key: "absent",
    label: "Absent(e)",
    description: returnDate
      ? `Reprise prévue le ${formatDate(returnDate)}`
      : "Date de reprise non précisée",
    icon: FaUserInjured,
    badge:
      "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20",
    card: "border-orange-200 bg-orange-50/70 dark:border-orange-500/20 dark:bg-orange-500/10",
  };
};

export const getAbsenceStats = (adherents = []) =>
  adherents.reduce(
    (acc, adherent) => {
      const status = getAbsenceStatus(adherent);

      if (status.key === "active") acc.active += 1;

      if (["absent", "returnToday", "returnLate"].includes(status.key)) {
        acc.absent += 1;
      }

      if (["returnToday", "returnLate"].includes(status.key)) {
        acc.toConfirm += 1;
      }

      return acc;
    },
    {
      active: 0,
      absent: 0,
      toConfirm: 0,
    },
  );

export const getReturnDateWarning = (dateReprise) => {
  if (!dateReprise) return null;

  const date = parseDate(dateReprise);
  const today = todayStart();

  if (!date) return null;

  if (date < today) {
    return "La date de reprise est dépassée. Pensez à confirmer le retour si l’adhérent a repris.";
  }

  if (date.getTime() === today.getTime()) {
    return "La reprise est prévue aujourd’hui. Pensez à confirmer le retour après le cours.";
  }

  return null;
};